/**
 * 게시판
 */
import { Fragment, useCallback, MouseEvent } from 'react';
import { setDateFormat, setYMDDateFormat } from '@utils/index';
import { SEARCH_PARAMS } from '@constants/router';
import type { ICommunityItem, ISetCommunityItem, l10nType } from '@type/index';

const getUpDownLevel = (value: number) => {
  const recomConfig = {
    // 1~2: Green
    recommendLevel1: 9,
    // 3~4: Blue, 5+: Red
    recommendLevel2: 19,
  };
  let level = '';

  if (0 < value && value <= recomConfig.recommendLevel1) {
    level = 'level1';
  } else if (recomConfig.recommendLevel1 < value && value <= recomConfig.recommendLevel2) {
    level = 'level2';
  } else if (recomConfig.recommendLevel2 < value) {
    level = 'level3';
  }
  return level;
};

export const setCommunityItem = (item: ICommunityItem, l10n: l10nType): ISetCommunityItem | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const { summary, writer, reactions, metaTags, id, rootBoard, timestamps, recentCommentWriter, emoticonUrl } = item;
  const { boardAlias, boardUrlPattern } = rootBoard?.board || {};
  const { recommendUpCount = 0, recommendDownCount = 0 } = reactions || {};
  const { postedAt, commentedAt } = timestamps || {};
  const url = boardUrlPattern?.replace('{boardAlias}', boardAlias)?.replace('{articleId}', id);
  const summaryFormat = summary?.replace(/<(\/)?([a-zA-Z0-9]*)(\s[a-zA-Z0-9]*=[^>]*)?(\s)*(\/)?>/gi, '');
  const isHideViewCount = Array.isArray(rootBoard?.board?.metaTags) && rootBoard?.board?.metaTags?.includes('hideViewCount');
  const gameUser = writer?.gameUser;
  const isHideComment = Array.isArray(metaTags) && (metaTags?.includes('NoticeFeedbackSurvey') || metaTags?.includes('CommentUiOff'));
  const commentWriter = recentCommentWriter ? recentCommentWriter?.gameUser?.gameCharacterName || recentCommentWriter?.loginUser?.name : '';
  const postedAtFormat = postedAt ? setYMDDateFormat(postedAt) : '';
  const commentAtFormat = commentedAt ? setDateFormat(commentedAt, l10n) : ''; // pc일때만 댓글 작성일자 노출

  // 작성자
  let writerName = '';
  let isWriterNameImage = writer?.admin && emoticonUrl && /^(http|https)/.test(emoticonUrl);
  if (writer?.admin) {
    // admin 이미지가 있는 경우
    writerName = isWriterNameImage ? emoticonUrl : writer?.loginUser?.name;
  } else if (gameUser?.gameCharacterName) {
    // 게임캐릭터가 있는 경우
    writerName = gameUser?.gameCharacterName;
  } else {
    // 게임캐릭터가 엾는 경우
    writerName = writer?.loginUser?.name;
  }

  // 업다운 아이콘
  let recommendUpDown = '';
  if (typeof recommendUpCount === 'number' && typeof recommendDownCount === 'number') {
    const maxCount = Math.max(recommendUpCount, recommendDownCount);
    if (maxCount > 0) {
      let type;
      let level = `-${getUpDownLevel(maxCount)}`;

      if (recommendUpCount > recommendDownCount) {
        type = 'up';
      } else if (recommendUpCount < recommendDownCount) {
        type = 'down';
      } else if (recommendUpCount === recommendDownCount) {
        type = 'same';
        level = '';
      }
      recommendUpDown = `${type}${level}`;
    }
  }

  return {
    ...item,
    url,
    summaryFormat,
    isHideViewCount,
    isHideComment,
    isWriterNameImage,
    writerName,
    recommendUpDown,
    commentWriter,
    postedAtFormat,
    commentAtFormat,
  };
};

const Comment = ({ count }: { count: number }) => {
  return (
    <span className="count-comment" data-count={count}>
      <span className="fe-svg comment_s"></span>
      <em>{count}</em>
    </span>
  );
};

const CommunityArticle = ({ item, l10n }: { item: ICommunityItem; l10n: l10nType }) => {
  const data = setCommunityItem(item, l10n);

  if (!data) {
    return <Fragment />;
  }

  const {
    rootBoard,
    url,
    thumbnailUrl,
    title,
    summaryFormat,
    writerName,
    reactions,
    isWriterNameImage,
    postedAtFormat,
    isHideViewCount,
    commentWriter,
    commentAtFormat,
    recommendUpDown,
  } = data || {};

  const clickHandler = useCallback((e: MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();

    const path = location.href?.split('?')[0];
    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams?.set(SEARCH_PARAMS.PAGE, '1');

    location.href = url;
    history.replaceState({ ...history.state }, '', `${path}?${newSearchParams?.toString()}`);
  }, []);

  return (
    <Fragment>
      <div className="search-item-content">
        {!!thumbnailUrl ? (
          <figure className="search-item-thumbnail">
            <a href={url} onClick={(e) => clickHandler(e, url)}>
              <img src={thumbnailUrl} alt="" />
            </a>
          </figure>
        ) : (
          <Fragment />
        )}
        <div className="search-item-article">
          <a href={url} onClick={(e) => clickHandler(e, url)}>
            <div className="search-item-title" dangerouslySetInnerHTML={{ __html: title }}></div>
            <div className="search-item-summary" dangerouslySetInnerHTML={{ __html: summaryFormat }}></div>
          </a>
          <div className="search-item-info">
            {writerName ? <span className="writer">{isWriterNameImage ? <img src={writerName} alt="" /> : writerName}</span> : ''}
            {rootBoard?.board?.boardName ? <span className="board">{rootBoard?.board?.boardName}</span> : ''}
            {postedAtFormat ? <span className="date">{postedAtFormat}</span> : ''}
          </div>
        </div>
      </div>
      <div className="search-item-reaction">
        {!isHideViewCount ? (
          <div className="view">
            <span className="fe-svg count_s"></span>
            <em>{reactions?.viewCount}</em>
          </div>
        ) : (
          <Fragment />
        )}
        {typeof reactions?.commentCount === 'number' ? (
          <div className="comment">
            <Comment count={reactions?.commentCount} />
            {commentWriter ? <span className="comment-writer">{commentWriter}</span> : ''}
            {commentWriter && commentAtFormat ? <span className="comment-date">{commentAtFormat}</span> : ''}
          </div>
        ) : (
          <Fragment />
        )}
        {recommendUpDown ? (
          <div className="recommend">
            <span className={`fe-svg recommend-${recommendUpDown}`}></span>
          </div>
        ) : (
          <Fragment />
        )}
      </div>
    </Fragment>
  );
};

export default CommunityArticle;
